import { useContext, useEffect, useState, useCallback } from "react";
import { NotifierContext } from "App/Notifier";
import { hideLoader, showLoader } from "redux/action/loaderAction";
import { useHistory, useParams } from "react-router-dom";
import { useTypedDispatch, useTypedSelector } from "App/Store";
import {
  formValueSelector,
  reduxForm,
  Field,
  FieldArray,
  InjectedFormProps,
  WrappedFieldProps,
} from "redux-form";
import { createBracketPlayoff } from "core/services/TournamnetBracket";
import InputWithLabel from "shared/components/redux-form/InputWithLabel";
import Select from "shared/components/redux-form/Select";
import { Button, Form, Toggle, Panel, Dropdown } from "rsuite";
import clsx from "clsx";
import Arrow from "shared/Icons/Arrow";
import LockIcon from "shared/Icons/LockIcon";
import LegacyArrowDownLineIcon from "@rsuite/icons/legacy/ArrowDownLine";
import { onlyNumber, maxLength } from "core/utils/Normalizer";
import validate from "./validate";
import BracketRoundFields from "components/AddBracket/BracketRound";
import { DEFAULT_API_ERROR } from "core/constants/Defaults";

interface UrlParams {
  tournamentId: string;
}

interface CombinedFormProps {
  bracketRounds?: any;
  participantCount: number;
  filteredParticipants: any[];
  isEditMode: boolean;
}

type FormValues = {
  bracketName: string;
  eligibleParticipants: string;
  bracketStyle: string;
  thirdPlaceMatch: boolean;
  bracketRounds: any;
  participants: { seed: number }[];
  matchFormat?: string;
  rankBy?: string;
  winScore?: number;
  tieScore?: number;
};

const ELIGIBLE_PARTICIPANTS = [
  { label: "All", value: "all" },
  { label: "Checked In", value: "checkedIn" },
];

const BRACKET_STYLE = [
  { label: "Single Elimination", value: "Single Elimination" },
  { label: "Double Elimination", value: "Double Elimination" },
  { label: "Round Robin", value: "Round Robin" },
];

const BEST_OF_OPTIONS = [
  { label: "Best Of 1", value: "BO1" },
  { label: "Best Of 3", value: "BO3" },
  { label: "Best Of 5", value: "BO5" },
];

const RANK_WISE_OPTIONS = [
  { label: "Matches Won", value: "Matches Won" },
  { label: "Custom Ranking", value: "Custom Ranking" },
];

const selector = formValueSelector("combined-bracket-form");

const handleSubmitFail = () => {
  setTimeout(() => {
    const firstElement = document.getElementsByClassName(
      "rs-error-message-wrapper"
    )[0];
    firstElement.scrollIntoView({ behavior: "smooth", block: "center" });
  }, 50);
};

const CombinedBracketForm = ({
  handleSubmit,
  initialize,
  change,
  bracketRounds,
  filteredParticipants,
  isEditMode,
  touch,
  invalid,
}: InjectedFormProps<FormValues, CombinedFormProps> & CombinedFormProps) => {
  const dispatch = useTypedDispatch();
  const { showNotification } = useContext(NotifierContext);
  const history = useHistory();
  const { tournamentId } = useParams<UrlParams>();
  const [selectedSeedType, setSelectedSeedType] = useState("Random");
  const [openSeedDropdown, setOpenSeedDropdown] = useState(false);
  const [showSeedForm, setShowSeedForm] = useState(false); // if edit mode show seed first

  const selectedBracketStyle = useTypedSelector((state) =>
    selector(state, "bracketStyle")
  );
  const selectedEligibleParticipants = useTypedSelector((state) =>
    selector(state, "eligibleParticipants")
  );
  const selectedBracketRounds = useTypedSelector((state) =>
    selector(state, "bracketRounds")
  );
  const selectedThirdPlaceMatch = useTypedSelector((state) =>
    selector(state, "thirdPlaceMatch")
  );
  const selectedBracketName = useTypedSelector((state) =>
    selector(state, "bracketName")
  );
  const selectedrankBy = useTypedSelector((state) => selector(state, "rankBy"));

  const selectedMatchFormat = useTypedSelector((state) =>
    selector(state, "matchFormat")
  );
  const selectedWinScore = useTypedSelector((state) =>
    selector(state, "winScore")
  );
  const selectedTieScore = useTypedSelector((state) =>
    selector(state, "tieScore")
  );
  const visibleParticipantCount = filteredParticipants?.length || 0;
  const roundSections = Object.entries(selectedBracketRounds || {});
  const roundConfigCount = roundSections.reduce((total, [, rounds]) => {
    return total + (Array.isArray(rounds) ? rounds.length : 0);
  }, 0);
  const minimumParticipantsRequired =
    selectedBracketStyle === "Round Robin" ? 3 : 4;
  const selectedParticipantsLabel =
    selectedEligibleParticipants === "checkedIn"
      ? "Checked In Teams"
      : "All Teams";
  const canProceedToSeed =
    (roundSections.length > 0 || selectedBracketStyle === "Round Robin") &&
    visibleParticipantCount >= minimumParticipantsRequired;
  const setupStats = [
    {
      label: "Participants",
      value: String(visibleParticipantCount),
    },
    {
      label: "Bracket Style",
      value: selectedBracketStyle || "Select",
    },
    {
      label: "Using",
      value: selectedParticipantsLabel,
    },
    {
      label: "Round Config",
      value:
        selectedBracketStyle === "Round Robin"
          ? "Shared"
          : String(roundConfigCount),
    },
  ];

  function handleBackRedirection() {
    history.goBack();
  }

  useEffect(() => {

    dispatch(showLoader())
    if (selectedBracketStyle === "Round Robin") {


      initialize({
        bracketRounds: {},
        bracketName: selectedBracketName,
        eligibleParticipants: selectedEligibleParticipants || "all",
        bracketStyle: selectedBracketStyle,
        thirdPlaceMatch: selectedThirdPlaceMatch || false,
        participants: filteredParticipants.map(() => ({ seed: 0 })),
        matchFormat: selectedMatchFormat,
        rankBy: selectedrankBy,
        winScore: selectedWinScore,
        tieScore: selectedTieScore,
      });
      dispatch(hideLoader())

      return;
    }

    // if (!bracketRounds) return;

    const roundsKey =
      selectedEligibleParticipants === "all" ? "allTeams" : "checkedInTeams";
    const roundsData = bracketRounds?.[roundsKey];

    const bracketRoundsValues: any = {};

    if (roundsData) {
      Object.entries(roundsData).forEach(([sectionKey, rounds]: any) => {
        bracketRoundsValues[sectionKey] = rounds.map(
          (round: any, index: number) => ({
            roundName: round.roundName || "",
            matchFormat: round.matchFormat || "BO1",
            roundSequence: round.roundSequence ?? index + 1,
          })
        );
      });
    }

    initialize({
      bracketRounds: bracketRoundsValues,
      bracketName: selectedBracketName,
      eligibleParticipants: selectedEligibleParticipants || "all",
      bracketStyle: selectedBracketStyle || "Single Elimination",
      thirdPlaceMatch: selectedThirdPlaceMatch || false,
      participants: filteredParticipants.map(() => ({ seed: 0 })),
    });

    dispatch(hideLoader())

  }, [
    bracketRounds,
    filteredParticipants,
    selectedEligibleParticipants,
    selectedBracketStyle,
    selectedThirdPlaceMatch,
    selectedTieScore,
    selectedrankBy,
    selectedMatchFormat,
    selectedWinScore,
    initialize,
  ]);

  const proceedToSeed = useCallback(() => {
    touch(
      "bracketName",
      "eligibleParticipants",
      "bracketStyle",
      "bracketRounds",
      "matchFormat",
      "rankBy",
      "winScore",
      "tieScore"
    );

    setTimeout(() => {
      if (!invalid) {
        setShowSeedForm(true);
      } else {
        handleSubmitFail();
      }
    }, 0);
  }, [invalid, touch]);
  const goBackToSettings = () => setShowSeedForm(false);

  function handleForm(formData: FormValues) {
    let seeds: string[] = [];

    if (selectedSeedType === "Manual") {
      const participantsWithSeed = filteredParticipants.map((p, index) => ({
        teamId: p.teamId,
        seed: formData.participants[index]?.seed || index + 1,
      }));

      const sortedBySeed = participantsWithSeed.sort((a, b) => a.seed - b.seed);
      seeds = sortedBySeed.map((p) => p.teamId);
    }

    let combinedData;
    if (selectedBracketStyle === "Round Robin") {
      combinedData = {
        bracketName: formData.bracketName,
        bracketType: formData.bracketStyle,
        bracketingOption: selectedSeedType,
        rounds: formData.bracketRounds,
        matchFormat: formData.matchFormat,
        rankBy: formData.rankBy,
        ...(formData.rankBy === "Custom Ranking" && {
          rankConfig: {
            winScore: formData.winScore,
            tieScore: formData.tieScore,
          },
        }),
      };
    } else {
      combinedData = {
        bracketName: formData.bracketName,
        bracketType: formData.bracketStyle,
        bracketingOption: selectedSeedType,
        thirdPlaceMatch: formData?.thirdPlaceMatch,
        seeds,
        rounds: formData.bracketRounds,
      };
    }

    dispatch(showLoader());
    createBracketPlayoff({ tournamentId, ...combinedData }).then((res) => {
      dispatch(hideLoader());
      if (res.status) {
        showNotification({
          message: "Playoff created successfully",
          title: "Success",
          type: "success",
        });
        history.replace("/");
      } else {
        showNotification({
          message: res.message || DEFAULT_API_ERROR,
          title: "Error",
          type: "error",
        });
      }
    });
  }

  function handleSeedDropdownToggle(e: any) {
    e.stopPropagation();
    setOpenSeedDropdown((prev) => !prev);
  }

  function handleSelectSeed(type: string) {
    setSelectedSeedType(type);
    setOpenSeedDropdown(false);

    if (type === "Manual") {
      filteredParticipants.forEach((_, index) => {
        change(`participants[${index}].seed`, index + 1);
      });
    }
  }

  function previewRedirect() {
    const url = `/tournaments/bracket-preview/${tournamentId}?bracketType=${selectedBracketStyle}&thirdPlaceMatch=${selectedThirdPlaceMatch}&type=${selectedEligibleParticipants}`;
    window.open(url, "_blank");
  }

  return (
    <Form fluid onSubmit={handleSubmit(handleForm)}>
      {!showSeedForm ? (
        <>
          <div className="add-bracket-form combined-bracket-page">
            <div className="add-bracket-form-title-wrapper">
              <span className="form-back-btn" onClick={handleBackRedirection}>
                <Arrow fontSize={32} />
              </span>
              <h4>{isEditMode ? "Edit Bracket" : "Create Bracket"}</h4>
            </div>

            <div className="section-wrapper">
              <div className="section-title">Create Bracket</div>
              <p className="combined-bracket-page__description">
                Choose the bracket basics first. Round settings below are loaded
                automatically for the selected bracket style and participant
                group.
              </p>
              <div className="combined-bracket-page__stats">
                {setupStats.map((stat) => (
                  <div
                    key={stat.label}
                    className="combined-bracket-page__stat-card"
                  >
                    <span className="combined-bracket-page__stat-label">
                      {stat.label}
                    </span>
                    <div className="combined-bracket-page__stat-value">
                      {stat.value}
                    </div>
                  </div>
                ))}
              </div>
              <div
                className={clsx(
                  "bracket-grid-form-wrapper",
                  "spacing",
                  "combined-bracket-page__settings-grid"
                )}
              >
                <Field
                  name="bracketName"
                  component={InputWithLabel}
                  placeholder="Enter Bracket Name"
                  label="Bracket Name"
                  normalize={(value: string) => maxLength(value, 50)}
                />
                <Field
                  name="eligibleParticipants"
                  component={Select}
                  cleanable
                  label="Eligible Participants"
                  options={ELIGIBLE_PARTICIPANTS}
                />
                <Field
                  name="bracketStyle"
                  component={Select}
                  cleanable
                  label="Bracket Style"
                  options={BRACKET_STYLE}
                />
                {selectedBracketStyle === "Single Elimination" && (
                  <div className="combined-bracket-page__toggle-card">
                    <div className="combined-bracket-page__toggle-copy">
                      <p className="combined-bracket-page__toggle-title">
                        Enable third position
                      </p>
                      <span className="combined-bracket-page__toggle-text">
                        Add a third-place match to this bracket.
                      </span>
                    </div>
                    <Field
                      name="thirdPlaceMatch"
                      type="checkbox"
                      component={({ input }: WrappedFieldProps) => (
                        <Toggle
                          checked={!!input.value}
                          onChange={input.onChange}
                        />
                      )}
                    />
                  </div>
                )}
                {selectedBracketStyle === "Round Robin" && (
                  <>
                    {" "}
                    <Field
                      name="matchFormat"
                      component={Select}
                      cleanable
                      label="Match Format"
                      options={BEST_OF_OPTIONS}
                    />
                    <Field
                      name="rankBy"
                      component={Select}
                      cleanable
                      label="Rank Wise"
                      options={RANK_WISE_OPTIONS}
                    />
                    {selectedrankBy === "Custom Ranking" && (
                      <>
                        {" "}
                        <Field
                          name="winScore"
                          component={InputWithLabel}
                          placeholder="Enter Win Points"
                          label="Win Points"
                          normalize={(value: string) => onlyNumber(value)}
                        />{" "}
                        <Field
                          name="tieScore"
                          component={InputWithLabel}
                          placeholder="Enter Tie Points"
                          label="Tie Points"
                          normalize={(value: string) => onlyNumber(value)}
                        />
                      </>
                    )}
                  </>
                )}
              </div>
            </div>

            {selectedBracketStyle !== "Round Robin" &&
              roundSections.map(([sectionKey, rounds]) => (
                <div
                  key={sectionKey}
                  className="section-wrapper combined-bracket-page__round-section"
                >
                  <div className="combined-bracket-page__round-section-header">
                    <div>
                      <div className="section-title">{sectionKey}</div>
                      <p className="combined-bracket-page__section-note">
                        Update the display name and match format for each round.
                      </p>
                    </div>
                    <span className="combined-bracket-page__round-count">
                      {Array.isArray(rounds) ? rounds.length : 0} rounds
                    </span>
                  </div>
                  <FieldArray
                    name={`bracketRounds.${sectionKey}`}
                    component={BracketRoundFields}
                    props={{ sectionKey }}
                  />
                </div>
              ))}
            {canProceedToSeed ? (
              <div className="bracket-submit-cta combined-bracket-page__cta">
                <Button
                  appearance="primary"
                  type="button"
                  onClick={proceedToSeed}
                  className="seed-button"
                >
                  Proceed to Seed Players
                </Button>
              </div>
            ) : (
              <div className="bracket-submit-cta combined-bracket-page__cta">
                <span className="bracket-err">
                  Minimum {minimumParticipantsRequired} participants required to
                  create a{" "}
                  {selectedBracketStyle || "selected"} bracket.
                </span>
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          {/* --- Seed Players --- */}
          <div className="bracket-settings-wrapper combined-bracket-page">
            <div className="bracket-settings-title-wrapper">
              <span className="form-back-btn" onClick={goBackToSettings}>
                <Arrow fontSize={32} />
              </span>
              <h4>Seed Players</h4>
            </div>
            <div className="combined-bracket-page__seed-header">
              <div>
                <div className="seed-player-title">Seed Players</div>
                <p className="combined-bracket-page__description">
                  Review the participant order before creating the playoff.
                  Choose a seeding method, then save when everything looks
                  right.
                </p>
              </div>
              {selectedBracketStyle !== "Round Robin" && (
                <div className="combined-bracket-page__seed-actions">
                  <Dropdown
                    renderToggle={() => (
                      <Button
                        type="button"
                        onClick={handleSeedDropdownToggle}
                        appearance="subtle"
                      >
                        {selectedSeedType}{" "}
                        <LegacyArrowDownLineIcon color="#fff" />
                      </Button>
                    )}
                    open={openSeedDropdown}
                  >
                    <Dropdown.Item onClick={() => handleSelectSeed("Random")}>
                      Random
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => handleSelectSeed("Manual")}>
                      Manual
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() => handleSelectSeed("Registered")}
                    >
                      Registered
                    </Dropdown.Item>
                  </Dropdown>
                  <Button type="button" onClick={previewRedirect}>
                    Bracket Preview
                  </Button>
                </div>
              )}
            </div>

            <div className="combined-bracket-page__stats combined-bracket-page__stats--compact">
              <div className="combined-bracket-page__stat-card">
                <span className="combined-bracket-page__stat-label">
                  Participants
                </span>
                <strong className="combined-bracket-page__stat-value">
                  {visibleParticipantCount}
                </strong>
              </div>
              <div className="combined-bracket-page__stat-card">
                <span className="combined-bracket-page__stat-label">
                  Bracket Style
                </span>
                <strong className="combined-bracket-page__stat-value">
                  {selectedBracketStyle || "Select"}
                </strong>
              </div>
              <div className="combined-bracket-page__stat-card">
                <span className="combined-bracket-page__stat-label">
                  Seed Mode
                </span>
                <strong className="combined-bracket-page__stat-value">
                  {selectedBracketStyle === "Round Robin"
                    ? "Registered"
                    : selectedSeedType}
                </strong>
              </div>
            </div>

            <div className="table-container">
              <div className="request-header">
                <div className="reg-title">Reg</div>
                <div className="checked-in-title">Checked in</div>
                <div className="participant-name-title">Participant name</div>
                <div className="seed-title">Seed</div>
                {/* <div className="placement-title">Placement Slot</div> */}
                <div className="action-title">
                  <LockIcon fontSize={20} />
                </div>
              </div>
              {filteredParticipants.map((participant, index) => (
                <Panel
                  key={participant.teamId || index}
                  bodyFill
                  className="seed-player-card-wrapper"
                >
                  <div className="content">
                    <div>{index + 1}</div>
                    <div>{participant.checkedIn ? "Yes" : "No"}</div>
                    <div>{participant.name}</div>
                    <div className="max-input">
                      <Field
                        name={`participants[${index}].seed`}
                        component={InputWithLabel}
                        placeholder="Enter Seed"
                        label=""
                        normalize={(v: string) => onlyNumber(v, 1, 1000000)}
                        disabled={selectedSeedType !== "Manual"}
                      />
                    </div>
                    {/* <div>{`Match ${index + 1} Slot`}</div> */}
                    <LockIcon fontSize={20} />
                  </div>
                </Panel>
              ))}
            </div>

            <div className="game-submit-cta combined-bracket-page__cta">
              <Button type="submit" appearance="primary">
                Save Seeds & Create Playoff
              </Button>
            </div>
          </div>
        </>
      )}
    </Form>
  );
};

export default reduxForm<FormValues, CombinedFormProps>({
  form: "combined-bracket-form",
  validate,
  onSubmitFail: handleSubmitFail,
})(CombinedBracketForm);
